import React from 'react';
import './ValidationDialog.css';

const ValidationDialog = ({ 
  isOpen, 
  onClose, 
  validationResults, 
  isLoading 
}) => {
  if (!isOpen) return null;

  const { data } = validationResults || {};
  const { inconsistencies = [], summary = {}, expectedFacts = {} } = data || {};

  // Get severity icon and color
  const getSeverityBadge = (severity) => {
    const badges = {
      high: { icon: '🔴', label: 'HIGH', color: '#dc3545' },
      medium: { icon: '🟡', label: 'MEDIUM', color: '#ffc107' },
      low: { icon: '🟢', label: 'LOW', color: '#28a745' }
    };
    return badges[severity] || badges.low;
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      drug: '💊',
      population: '👥',
      design: '📋',
      endpoint: '🎯'
    };
    return icons[category] || '📄';
  };

  return (
    <div className="validation-dialog-overlay" onClick={onClose}>
      <div className="validation-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="validation-dialog-header">
          <h2>
            <span className="validation-icon">🔍</span>
            Validation Results
          </h2>
          <button className="validation-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="validation-dialog-content">
          {isLoading ? (
            <div className="validation-loading">
              <div className="spinner"></div>
              <p>Validating sections against Knowledge Graph...</p>
              <p className="validation-loading-subtext">This may take a few moments</p>
            </div>
          ) : (
            <>
              {/* Summary Section */}
              <div className="validation-summary">
                <div className="validation-summary-card">
                  <div className="validation-summary-icon">
                    {summary.total === 0 ? '✅' : '⚠️'}
                  </div>
                  <div className="validation-summary-content">
                    <h3>
                      {summary.total === 0 
                        ? 'All Checks Passed!' 
                        : `${summary.total} ${summary.total === 1 ? 'Issue' : 'Issues'} Found`
                      }
                    </h3>
                    <p>
                      {summary.total === 0 
                        ? 'Your protocol sections are consistent with the Knowledge Graph.'
                        : 'Review the inconsistencies below and make necessary corrections.'
                      }
                    </p>
                  </div>
                </div>

                {summary.total > 0 && (
                  <div className="validation-summary-breakdown">
                    <div className="validation-summary-item high">
                      <span className="count">{summary.high || 0}</span>
                      <span className="label">High Priority</span>
                    </div>
                    <div className="validation-summary-item medium">
                      <span className="count">{summary.medium || 0}</span>
                      <span className="label">Medium Priority</span>
                    </div>
                    <div className="validation-summary-item low">
                      <span className="count">{summary.low || 0}</span>
                      <span className="label">Low Priority</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Expected Facts Section (Collapsible) */}
              {expectedFacts && Object.keys(expectedFacts).length > 0 && (
                <details className="validation-expected-facts">
                  <summary>
                    📚 Expected Facts from Knowledge Graph
                    <span className="toggle-icon">▼</span>
                  </summary>
                  <div className="validation-expected-facts-content">
                    {expectedFacts.drug && (
                      <div className="fact-section">
                        <h4>💊 Drug Information</h4>
                        <ul>
                          {expectedFacts.drug.name && <li><strong>Name:</strong> {expectedFacts.drug.name}</li>}
                          {expectedFacts.drug.dose && <li><strong>Dose:</strong> {expectedFacts.drug.dose}</li>}
                          {expectedFacts.drug.route && <li><strong>Route:</strong> {expectedFacts.drug.route}</li>}
                          {expectedFacts.drug.frequency && <li><strong>Frequency:</strong> {expectedFacts.drug.frequency}</li>}
                        </ul>
                      </div>
                    )}
                    {expectedFacts.population && (
                      <div className="fact-section">
                        <h4>👥 Population Information</h4>
                        <ul>
                          {expectedFacts.population.ageMin && <li><strong>Minimum Age:</strong> {expectedFacts.population.ageMin} years</li>}
                          {expectedFacts.population.sampleSize && <li><strong>Sample Size:</strong> {expectedFacts.population.sampleSize}</li>}
                          {expectedFacts.population.ecogStatus && <li><strong>ECOG Status:</strong> {expectedFacts.population.ecogStatus}</li>}
                        </ul>
                      </div>
                    )}
                    {expectedFacts.design && (
                      <div className="fact-section">
                        <h4>📋 Study Design</h4>
                        <ul>
                          {expectedFacts.design.phase && <li><strong>Phase:</strong> {expectedFacts.design.phase}</li>}
                          {expectedFacts.design.type && <li><strong>Type:</strong> {expectedFacts.design.type}</li>}
                          {expectedFacts.design.blinding && <li><strong>Blinding:</strong> {expectedFacts.design.blinding}</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Inconsistencies List */}
              {inconsistencies.length > 0 ? (
                <div className="validation-inconsistencies">
                  <h3 className="validation-section-title">
                    ⚠️ Inconsistencies Detected
                  </h3>
                  <div className="validation-inconsistencies-list">
                    {inconsistencies.map((issue, index) => {
                      const badge = getSeverityBadge(issue.severity);
                      const categoryIcon = getCategoryIcon(issue.category);
                      
                      return (
                        <div 
                          key={index} 
                          className={`validation-issue validation-issue-${issue.severity}`}
                        >
                          <div className="validation-issue-header">
                            <div className="validation-issue-badge">
                              <span className="badge-icon">{badge.icon}</span>
                              <span 
                                className="badge-label" 
                                style={{ color: badge.color }}
                              >
                                {badge.label}
                              </span>
                            </div>
                            <div className="validation-issue-meta">
                              <span className="issue-category">
                                {categoryIcon} {issue.category}
                              </span>
                              <span className="issue-section">
                                Section {issue.section}
                              </span>
                            </div>
                          </div>
                          
                          <div className="validation-issue-body">
                            <p className="validation-issue-message">
                              <strong>{issue.field}:</strong> {issue.message}
                            </p>
                            
                            <div className="validation-issue-details">
                              {issue.type === 'conflict' && (
                                <>
                                  <div className="detail-item expected">
                                    <span className="detail-label">Expected (Knowledge Graph):</span>
                                    <span className="detail-value">{issue.expected || 'N/A'}</span>
                                  </div>
                                  <div className="detail-item found">
                                    <span className="detail-label">Found in Section:</span>
                                    <span className="detail-value">{issue.found || 'N/A'}</span>
                                  </div>
                                </>
                              )}
                              {issue.type === 'missing' && (
                                <div className="detail-item missing">
                                  <span className="detail-label">Missing Information:</span>
                                  <span className="detail-value">{issue.expected || 'N/A'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="validation-issue-actions">
                            <button 
                              className="btn-view-section"
                              onClick={() => {
                                // Scroll to section (implement later)
                                console.log('Navigate to section:', issue.section);
                              }}
                            >
                              📄 View Section
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                !isLoading && summary.total === 0 && (
                  <div className="validation-success">
                    <div className="validation-success-icon">✅</div>
                    <h3>Perfect! No Inconsistencies Found</h3>
                    <p>All protocol sections are aligned with your Knowledge Graph.</p>
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="validation-dialog-footer">
          {!isLoading && (
            <>
              <div className="validation-footer-info">
                {data?.validatedAt && (
                  <span className="validation-timestamp">
                    ⏰ Validated: {new Date(data.validatedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <button className="btn-close-dialog" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationDialog;



